import { RouteNames } from '../../types/RouteNames'

const withOperationalBaseRoute = <T extends string>(subPath: T) =>
  `/operational${subPath}` as const

export const operationalDynamicRoutes = {
  profileInfo: <T extends string>(idClient: T) =>
    withOperationalBaseRoute(`/profile-info/${idClient}`),
  ticketDetails: <T extends string>(ticketId: T) =>
    withOperationalBaseRoute(`/ticket-details/${ticketId}`),
  agentTicketArea: <T extends string>(areaId: T) =>
    withOperationalBaseRoute(`/agent-ticket/area/${areaId}`),
  agentTicketAreaCompleted: <T extends string>(areaId: T) =>
    withOperationalBaseRoute(`/agent-ticket/area/${areaId}/completed`),
  groupDetails: <T extends string>(groupId: T) =>
    withOperationalBaseRoute(`/groups?filter=&ALL&groupId=${groupId}`),

  schedule: <T extends string>(patientId: T) =>
    withOperationalBaseRoute(`/${patientId}/schedule`),
  gallery: <T extends string>(patientId: T) =>
    withOperationalBaseRoute(`/patient/${patientId}/gallery`),
  galleryItem: <T extends string>(patientId: T) =>
    withOperationalBaseRoute(`/patient/${patientId}/gallery/:id`),
  galleryFolder: <T extends string>(patientId: T) =>
    withOperationalBaseRoute(`/patient/${patientId}/folder/:id`),
  config: <T extends string>(patientId: T) =>
    withOperationalBaseRoute(`/${patientId}/config-client`),
  patientTicket: <T extends string>(patientId: T) =>
    withOperationalBaseRoute(`/${patientId}/ticket`),
  groupsFeed: <T extends string>(patientId: T) =>
    withOperationalBaseRoute(`/${patientId}/groups-feed`),
  medicalSheet: <T extends string>(patientId: T) =>
    withOperationalBaseRoute(`/${patientId}/medical-sheet`),
  bathroom: <T extends string>(patientId: T) =>
    withOperationalBaseRoute(`/patient/${patientId}/bathroom`),
  patientBelongs: <T extends string>(patientId: T) =>
    withOperationalBaseRoute(`/patient/${patientId}/belongs`),
  food: <T extends string>(patientId: T) =>
    withOperationalBaseRoute(`/patient/${patientId}/food`),
  foodReport: <T extends string>(patientId: T) =>
    withOperationalBaseRoute(`/patient/${patientId}/foodReport`),
  liquids: <T extends string>(patientId: T) =>
    withOperationalBaseRoute(`/patient/${patientId}/liquids`),
  restriction: <T extends string>(patientId: T) =>
    withOperationalBaseRoute(`/patient/${patientId}/restriction`),
  sleep: <T extends string>(patientId: T) =>
    withOperationalBaseRoute(`/patient/${patientId}/sleep`),
  remedy: <T extends string>(patientId: T) =>
    withOperationalBaseRoute(`/patient/${patientId}/remedy`),
  bodyInspection: <T extends string>(patientId: T) =>
    withOperationalBaseRoute(`/patient/${patientId}/body-inspection`),
  comments: <T extends string>(patientId: T) =>
    withOperationalBaseRoute(`/patient/${patientId}/comments`),
  interferingBehavior: <T extends string>(patientId: T) =>
    withOperationalBaseRoute(`/patient/${patientId}/interfering-behavior`),
  caaClient: <T extends string>(patientId: T) =>
    withOperationalBaseRoute(`/${patientId}/caa`),
  photos: <T extends string>(patientId: T) =>
    withOperationalBaseRoute(`/${patientId}/photos`)
}

export const operationalRouteNames = {
  home: withOperationalBaseRoute(''),
  fileEditor: withOperationalBaseRoute('/fileEditor'),
  profile: withOperationalBaseRoute('/profile'),

  feed: withOperationalBaseRoute('/feed'),
  request: withOperationalBaseRoute('/request'),
  patients: withOperationalBaseRoute('/patients'),
  entryPatient: withOperationalBaseRoute('/patient/:patientId/*'),
  session: withOperationalBaseRoute('/session'),
  patientsAbsences: withOperationalBaseRoute('/patients-absences'),
  frequenciesManager: withOperationalBaseRoute('/frequencies'),
  belongManager: withOperationalBaseRoute('/belongs'),
  remedyManager: withOperationalBaseRoute('/remedy'),
  medicalRecordManagement: withOperationalBaseRoute(
    '/medical-record-management'
  ),
  tasks: withOperationalBaseRoute('/tasks'),
  taskDetails: withOperationalBaseRoute('/tasks/:id'),
  ticket: withOperationalBaseRoute('/ticket'),
  agentTicket: withOperationalBaseRoute('/agent-ticket'),
  explore: withOperationalBaseRoute('/explore'),
  agentTicketArea: operationalDynamicRoutes.agentTicketArea(':areaId'),
  agentTicketAreaCompleted:
    operationalDynamicRoutes.agentTicketAreaCompleted(':areaId'),
  ticketDetails: operationalDynamicRoutes.ticketDetails(':ticketId'),
  ticketChat: withOperationalBaseRoute('/ticket-chat'),
  profileInfo: operationalDynamicRoutes.profileInfo(':idClient'),

  chat: withOperationalBaseRoute('/chat'),
  groups: withOperationalBaseRoute('/groups'),
  documents: withOperationalBaseRoute('/documents'),
  occurrences: withOperationalBaseRoute('/occurrences'),
  caa: withOperationalBaseRoute('/caa'),
  interferingBehavior: withOperationalBaseRoute('/interfering-behavior'),
  photos: withOperationalBaseRoute('/photos'),
  groupDetails: withOperationalBaseRoute('/groups/:groupId'),
  groupDrive: withOperationalBaseRoute('/groups/:groupId/drive'),
  groupDriveItem: withOperationalBaseRoute('/groups/:groupId/drive/:id'),
  groupDriveFolder: withOperationalBaseRoute(
    '/groups/:groupId/drive/folder/:id'
  ),
  postDetails: withOperationalBaseRoute('/post-details/:id/:commentId?'),

  clientSchedule: operationalDynamicRoutes.schedule(':patientId'),
  gallery: operationalDynamicRoutes.gallery(':patientId'),
  galleryItem: operationalDynamicRoutes.galleryItem(':patientId'),
  galleryFolder: operationalDynamicRoutes.galleryFolder(':patientId'),
  configClient: operationalDynamicRoutes.config(':patientId'),
  groupsFeed: operationalDynamicRoutes.groupsFeed(':patientId'),
  medicalSheet: operationalDynamicRoutes.medicalSheet(':patientId'),
  bathroom: operationalDynamicRoutes.bathroom(':patientId'),
  patientBelongs: operationalDynamicRoutes.patientBelongs(':patientId'),
  food: operationalDynamicRoutes.food(':patientId'),
  foodReport: operationalDynamicRoutes.foodReport(':patientId'),
  liquids: operationalDynamicRoutes.liquids(':patientId'),
  restriction: operationalDynamicRoutes.restriction(':patientId'),
  sleep: operationalDynamicRoutes.sleep(':patientId'),
  remedy: operationalDynamicRoutes.remedy(':patientId'),
  bodyInspection: operationalDynamicRoutes.bodyInspection(':patientId'),
  patientTicket: operationalDynamicRoutes.patientTicket(':patientId'),
  comments: operationalDynamicRoutes.comments(':patientId'),
  caaClient: operationalDynamicRoutes.caaClient(':patientId'),
  photosClient: operationalDynamicRoutes.photos(':patientId'),
  interferingBehaviorClient:
    operationalDynamicRoutes.interferingBehavior(':patientId')
} satisfies RouteNames
